/* eslint-disable react-hooks/exhaustive-deps */
import { Button, createStyles, Divider, Grid, makeStyles, TextField, Theme } from '@material-ui/core';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import { FormApi } from 'final-form';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { AnyObject, Field, Form, FormSpy } from 'react-final-form';
import { showNotification } from '../../App';
import { CollapsibleSectionCard } from '../../components/cards/CollapsibleSectionCard';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { KeyboardTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

const SECTION_COLOR = '#1565c0';
const SECTION_BG = '#e3f2fd';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flex: 1,
            flexFlow: 'column',
            maxWidth: '800px',
        },
        textField: {
            width: '100%'
        },
        actionsRow: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing(1.5),
            marginTop: theme.spacing(3),
        },
        actionsDivider: {
            marginTop: theme.spacing(3),
        },
        formGroup: {
            alignItems: 'flex-start',
            '& .MuiFormControlLabel-root': {
                marginLeft: 0,
            }
        },
        text : {
            marginRight: '20px'
        }
    })
);

interface FormState {
    marketingAutomationSendTime: MaterialUiPickersDate;
    maximumMailoutLimit: number | null;
}
interface MarketingSettingsTabState {
    isLoading: boolean;
    isFormChanged: boolean;
    onSubmitLoading: boolean;
}

export default function MarketingSettingsTab() {

    const classes = useStyles();

    const [state, setState] = useState<MarketingSettingsTabState>({
        isLoading: true,
        isFormChanged: false,
        onSubmitLoading: false,
    });

    const [formState, setFormState] = useState<FormState>({
        marketingAutomationSendTime: null,
        maximumMailoutLimit: null
    });

    const marketingSettings = useQuery<MarketingSettingsData>(MARKETING_SETTINGS_QUERY);

    // tslint:disable-next-line: no-any
    const [updateMarketingSettings] = useMutation<any, MarketingSettingsMutationParams>(MARKETING_SETTINGS_MUTATION);

    useEffect(() => {
    
        // tslint:disable-next-line: max-line-length
        if (!marketingSettings.loading && marketingSettings.data) {

            var marketingAutomationSendTime = marketingSettings.data?.settings.systems.marketingSettings.config?.marketingAutomationSendTime ?? null;
            var maximumMailoutLimit = marketingSettings.data?.settings.systems.marketingSettings.config?.maximumMailoutLimit ?? null;
            var isMarketingAutomationSendTimeValid = moment(marketingAutomationSendTime, 'HH:mm').isValid();

            setFormState((prevFormState) => {
                return {
                    ...prevFormState,
                    marketingAutomationSendTime: isMarketingAutomationSendTimeValid ? moment(marketingAutomationSendTime, 'HH:mm') : null,
                    maximumMailoutLimit: maximumMailoutLimit
                };
            });

            setState((prevState) => {
                return {
                    ...prevState,
                    isLoading: false
                };
            });
        }
    },[marketingSettings.data, marketingSettings.loading]);

    // tslint:disable-next-line: no-any
    const onFormValueChanged = (changeProps: any) => {
        if (!changeProps.pristine) {
            setState((prevState) => {
                return {
                    ...prevState,
                    isFormChanged: true
                };
            });
        }
    };

    // tslint:disable-next-line: no-any
    // const required = (value: any) => {
    //     return value ? undefined : 'Required';
    // };

    const onReset = (form: FormApi<FormState>) => () => {
        form.setConfig('keepDirtyOnReinitialize', false);
        form.batch(() => {
            form.reset();
        });
        form.setConfig('keepDirtyOnReinitialize', true);
    };

    const onSubmit = (form: FormApi<FormState>, values: AnyObject) => {
        // console.log('submit');
        setState((prevState) => {
            return {
                ...prevState,
                onSubmitLoading: true,
            };
        });

        updateMarketingSettings({
            variables: {
                marketingAutomationSendTime: values.marketingAutomationSendTime ? moment(values.marketingAutomationSendTime).format('HH:mm') : null,
                maximumMailoutLimit: values.maximumMailoutLimit
            }
        })
        // tslint:disable-next-line: no-any
        .then((results: { data: any }) => {
            if (results.data) {
                showNotification(null, 'Successfully submitted', 'info');     

                form.setConfig('keepDirtyOnReinitialize', false);

                const tempInitial: FormState = {
                    marketingAutomationSendTime: values.marketingAutomationSendTime,
                    maximumMailoutLimit: values.maximumMailoutLimit
                };

                form.batch(() => {
                    form.reset(tempInitial);
                });
                
                form.setConfig('keepDirtyOnReinitialize', true);

                setState((_prevState) => {
                    return {
                        ..._prevState,
                        onSubmitLoading: false
                    };
                });
            }
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            showNotification('Failed to update marketing settings', reason, 'error');
            setState((_prevState) => {
                return {
                    ..._prevState,
                    onSubmitLoading: false
                };
            });
        });
    };

    return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
        <div className={classes.root}>
            {state.isLoading ? (
                <SkeletonLoader variant="form" rows={4} columns={1} />
            ) : (
                <Form
                    onSubmit={(values, form: FormApi<FormState>) => onSubmit(form, values)}  
                    initialValues={formState}
                    initialValuesEqual={() => true}
                    keepDirtyOnReinitialize={true}
                    subscription={{
                        submitting: true,
                        pristine: true
                    }}
                    render={({
                        handleSubmit,
                        form,
                        submitting,
                        pristine,
                        values
                    }) => (
                            <form onSubmit={handleSubmit} id="marketingTab">
                                <FormSpy 
                                    subscription={{ pristine: true }}
                                    // tslint:disable-next-line: no-shadowed-variable
                                    onChange={props => {
                                        onFormValueChanged(props);
                                    }}
                                />

                                <CollapsibleSectionCard
                                    title="Marketing Settings"
                                    description="Set the time marketing emails go out automatically and cap how many can be sent in a mailout."
                                    icon={AnnouncementIcon}
                                    color={SECTION_COLOR}
                                    bgColor={SECTION_BG}
                                >
                                <Grid container={true} spacing={3}>
                                    <Grid item={true} xs={12} md={12} lg={12} sm={12}>
                                        <span className={classes.text}>Marketing Automation Send Time</span>
                                        <Field
                                                name="marketingAutomationSendTime"
                                                allowNull={true}
                                                // validate={form.getFieldState('timeOfDay')?.value !== null ? required : undefined}
                                            >
                                                {({ input, meta }) => (
                                                    <KeyboardTimePicker
                                                        {...input}
                                                        label=""
                                                        error={meta.error && meta.touched}
                                                        helperText={meta.error && meta.touched ? meta.error : ''}
                                                        size="small"
                                                        variant="inline"
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            </Field>
                                    </Grid>
                                    <Grid item={true} xs={12} md={12} lg={12} sm={12}>
                                        <span className={classes.text}>Maximum Mailout Limit</span>
                                        <Field
                                                name="maximumMailoutLimit"
                                                allowNull={true}
                                                // validate={form.getFieldState('timeOfDay')?.value !== null ? required : undefined}
                                            >
                                                {({ input, meta }) => (
                                                    <TextField
                                                        {...input}
                                                        label=""
                                                        error={meta.error && meta.touched}
                                                        helperText={meta.error && meta.touched ? meta.error : ''}
                                                        size="small"
                                                        type="number"    
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            </Field>
                                    </Grid>
                                    {/* {printJson(values)} */}
                            </Grid>
                                </CollapsibleSectionCard>
                                <Divider className={classes.actionsDivider} />
                                <div className={classes.actionsRow}>
                                    <Button
                                        variant="text"
                                        color="default"
                                        type="button"
                                        disabled={submitting || pristine}
                                        onClick={onReset(form)}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={submitting || pristine || state.onSubmitLoading}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </form>
                        )}
                />
            )}
        </div>
        </MuiPickersUtilsProvider>
    );
}

export interface MarketingSettingsData {
    settings: Settings;
}

export interface Settings {
    systems: Systems;
}

export interface Systems {
    id: number;
    marketingSettings: MarketingSettings;
}

export interface MarketingSettings {
    id: number;
    config: Config;
}

export interface Config {
    marketingAutomationSendTime: string;
    maximumMailoutLimit: number | null;
}

const MARKETING_SETTINGS_QUERY = gql`
    query applicationSettings {
        settings {
            id
            systems {
                id
                marketingSettings {
                    config {
                        marketingAutomationSendTime
                        maximumMailoutLimit
                    }
                }
            }
        }
    }
`;

interface MarketingSettingsMutationParams {
    marketingAutomationSendTime: string | null;
    maximumMailoutLimit: number | null;
}

const MARKETING_SETTINGS_MUTATION = gql`
    mutation UpdateMarketingSettings (
        $marketingAutomationSendTime: String
        $maximumMailoutLimit: Int
    ) {
        applicationSettings {
            marketingSettings {
                update (
                    marketingAutomationSendTime: $marketingAutomationSendTime
                    maximumMailoutLimit: $maximumMailoutLimit
                ) {
                    error
                    status
                }
            }
        }
    }
`;